import { createEntityQuery, Order } from '@datorama/akita';
import { map } from 'rxjs';
import store from './store';
import { type Organisation } from 'types/models';

// Case-insensitive, locale-aware sort by name (fallback to code). Applied at every tree level
// so the whole hierarchy renders in a consistent, predictable order.
const compareByDisplayName = (a: Organisation, b: Organisation): number =>
   (a?.name ?? a?.code ?? '').localeCompare(b?.name ?? b?.code ?? '', undefined, { sensitivity: 'base' });

function nestOrganisations(items: Organisation[], id: Organisation['id'] | null = null): Organisation[] {
   // we want to find the root of the tree. In case of rights on individual orgs, that is not always possible
   // we need to find either an org that has no parents or an org that has a parent that we do not know
   if (id == null) {
      const itemsWithoutParents = items.filter((item) => (item?.parents ?? []).length === 0);

      // if only one org has no parent, we can determine the tree
      if (itemsWithoutParents.length === 1) {
         return itemsWithoutParents.map((item) => ({ ...item, children: nestOrganisations(items, item.id) })).sort(compareByDisplayName);
      }

      const itemsWithoutKnownParents = items.filter((item) => (item?.parents ?? []).some((parent) => !items.find((i) => i.id === parent.id)));

      // of only one item has an unknown parent, we can determine the tree
      if (itemsWithoutKnownParents.length === 1) {
         return itemsWithoutKnownParents.map((item) => ({ ...item, children: nestOrganisations(items, item.id) })).sort(compareByDisplayName);
      }
   }

   return items
      .filter((item) => (item?.parents ?? []).find((parent) => parent?.id === id))
      .map((item) => ({ ...item, children: nestOrganisations(items, item.id) }))
      .sort(compareByDisplayName);
}

export const query = createEntityQuery(store, {
   sortBy: 'id',
   sortByOrder: Order.ASC,
});

export const allEntities$ = query.selectAll().pipe(
   map((organisations) =>
      organisations.map((organisation) => ({
         ...organisation,
         label: organisation.code ? `(${organisation.code}) ${organisation.name}` : organisation.name,
         value: organisation.id,
      }))
   )
);

export const entities$ = allEntities$.pipe(map((organisations) => organisations.filter((org) => !org.deletedAt)));

export const activeEntity$ = query.selectActive();

export const allEntitiesTree$ = allEntities$.pipe(map((organisations) => nestOrganisations(organisations)));

export const entityTree$ = entities$.pipe(map((organisations) => nestOrganisations(organisations ?? [])));

export const statistics$ = query.select('statistics');
export const loading$ = query.selectLoading();

export const entitiesBeingDeleted$ = query.selectAll({
   filterBy: (entity) => !!entity?.deleteOperationStatus,
});
